
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import { browseModeStore, globalStore, uiStore } from "@/store/store";
import AppLayout from "@/components/Reusable/AppLayout/AppLayout.vue";
import TopBar from "@/components/Reusable/AppLayout/TopBar/TopBar.vue";
import AppLayoutBodyPanelArea from "@/components/Reusable/AppLayout/AppLayoutBodyPanelArea.vue";
import TopBarSection from "@/components/Reusable/AppLayout/TopBar/TopBarSection.vue";
import IframeWrapper from "@/components/Reusable/Iframe/IframeWrapper.vue";
import { CanvasPreviewWidth } from "@/types/ui";
import IconButton from "@/components/UI/IconButton.vue";
import StandardButton from "@/components/UI/StandardButton.vue";
import Icon from "@/components/UI/Icon.vue";
import {
  disableBrowseMode,
  handleBrowseModeIframeLoaded,
  updateBrowseModePageTitle
} from "@/lib/browse-mode";
import BreakpointsSelection from "@/components/Builder/TopBar/BreakpointsSelection.vue";
import {
  handleGlobalSelectorsChanged,
  handleGlobalSettingsChanged
} from "@/lib/canvas/reacting-to-global-store-changes";
import BrowseModeSidebarPanels from "@/components/BrowseMode/BrowseModeSidebarPanels.vue";
import { isUiPanelOpen, openUiPanel, toggleUiPanel } from "@/lib/panels/panels";
import ClosingConfirmations from "@/components/Reusable/AppLayout/ClosingConfirmations.vue";
import { getMinimumWidth } from "@/lib/breakpoints/breakpoints";
import {
  removeChangesNotSavedWarning,
  warnIfExitingWithUnsavedChanges
} from "@/util/warn-unsaved-changes";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import BrowseModeSave from "@/components/BrowseMode/BrowseModeSave.vue";
import UndoRedo from "@/components/Builder/TopBar/UndoRedo.vue";
import {
  initializeAppWhenInitialModeIsBrowse,
  loadConfigFromWordPress
} from "@/lib/wordpress/load";
import Loading from "@/components/Reusable/Loading.vue";
import { Breakpoint } from "@/types/breakpoints";
import { listenForNonceUpdates } from "@/util/nonce-broadcast";

@Component({
  components: {
    UndoRedo,
    BrowseModeSave,
    BrowseModeSidebarPanels,
    BreakpointsSelection,
    Icon,
    StandardButton,
    IframeWrapper,
    TopBarSection,
    AppLayoutBodyPanelArea,
    TopBar,
    AppLayout,
    IconButton,
    ClosingConfirmations,
    Dropdown,
    Loading
  }
})
export default class BrowseMode extends Vue {
  readonly iframeID = "breakdance-browser-iframe";

  iframeDocument: Document | null = null;
  warnIfUnsavedFunction!: (e: Event) => void;

  broadcastChannel: BroadcastChannel | null = null;
  async beforeMount() {
    if (window.Breakdance.initialMode === "browse") {
      const maybeLoadedData = await loadConfigFromWordPress();
      initializeAppWhenInitialModeIsBrowse(maybeLoadedData, {
        returnUrl: window.Breakdance.returnUrl,
        browseModeOpenUrl: window.Breakdance.browseModeOpenUrl
      });
    }

    this.broadcastChannel = listenForNonceUpdates();
  }

  get interfaceState() {
    return uiStore.interfaceState;
  }

  mounted() {
    this.warnIfUnsavedFunction = warnIfExitingWithUnsavedChanges(
      () =>
        globalStore.settingsHasUnsavedChangesPresent ||
        globalStore.selectorsHasUnsavedChangesPresent
    );
  }

  beforeDestroy() {
    removeChangesNotSavedWarning(this.warnIfUnsavedFunction);
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
  }

  handleIframeLoaded(iframe: HTMLIFrameElement | null) {
    if (iframe) {
      this.iframeDocument = handleBrowseModeIframeLoaded(iframe);
    } else {
      this.iframeDocument = null;
      throw "Unable to proceed – iframe element is missing";
    }
  }

  handlePreviewWidthChange(previewWidth: CanvasPreviewWidth) {
    uiStore.changePreviewWidthMutation(previewWidth);
  }

  handlePreviewWidthChangeWithAdjustmentBar(previewWidthInPx: number) {
    uiStore.changePreviewWidthWithAdjustmentBar(previewWidthInPx);
  }

  handleResetPreviewWidth() {
    uiStore.selectBaseBreakpoint();
  }

  get iframeUrl(): string | null {
    return browseModeStore.iframeUrl;
  }

  get iframeState() {
    return browseModeStore.iframeState;
  }

  get iframeDocumentTitleToDisplay() {
    return browseModeStore.iframeDocumentTitleToDisplay;
  }

  get iframeUrlToDisplay() {
    return browseModeStore.iframeUrlToDisplay;
  }

  get iframePreviewWidth() {
    return uiStore.canvasPreviewWidth;
  }

  get iframeMinWidth() {
    return getMinimumWidth(globalStore);
  }

  get leftSidebarPanelState() {
    return uiStore.panelState.leftSidebarPanel;
  }

  get rightSidebarPanelState() {
    return uiStore.panelState.rightSidebarPanel;
  }

  get globalSettings() {
    return globalStore.globalSettings;
  }

  get selectors() {
    return globalStore.cssSelectors;
  }

  get isAnyBrowseModeSupportedPanelOpen() {
    return (
      isUiPanelOpen("globalSettings") ||
      isUiPanelOpen("selectors") ||
      isUiPanelOpen("history")
    );
  }

  get currentBreakpoint() {
    return uiStore.currentBreakpoint;
  }

  set currentBreakpoint(breakpoint: Breakpoint) {
    uiStore.changeBreakpoint(breakpoint);
  }

  exitBrowseMode() {
    disableBrowseMode();
  }

  toggleGlobalSettingsPanel() {
    toggleUiPanel("globalSettings");
  }

  toggleSelectorsPanel() {
    toggleUiPanel("selectors");
  }

  toggleHistoryAndRevisionsPanel() {
    openUiPanel("history");
  }

  @Watch("iframeDocumentTitleToDisplay", { immediate: true })
  handleCurrentDocumentChange() {
    updateBrowseModePageTitle();
  }

  @Watch("globalSettings", { deep: true, immediate: true })
  onGlobalSettingsChanged() {
    if (this.iframeDocument) {
      handleGlobalSettingsChanged(this.iframeDocument);
    }
  }

  @Watch("selectors", { deep: true, immediate: true })
  onSelectorsChanged() {
    if (this.iframeDocument) {
      handleGlobalSelectorsChanged(this.iframeDocument);
    }
  }
}
